import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Library as LibIcon, Plus, Trash2, Pencil, Search, Filter, Hammer, Package, Users as UsersIcon, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { k: "work", label: "Work Items", icon: Hammer, color: "text-blue-600 bg-blue-50 border-blue-200" },
  { k: "material", label: "Materials", icon: Package, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  { k: "labour", label: "Labour Rates", icon: UsersIcon, color: "text-amber-600 bg-amber-50 border-amber-200" },
  { k: "template", label: "Templates", icon: LayoutTemplate, color: "text-purple-600 bg-purple-50 border-purple-200" },
];

const emptyForm = { kind: "work", name: "", code: "", description: "", unit: "Nos", rate: 0, gst_percent: 18, category: "General", trade: "", vendor_id: "", is_active: true };

export default function Library() {
  const [kind, setKind] = useState("work");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editId, setEditId] = useState(null);

  const load = () => {
    const params = { kind };
    if (q) params.q = q;
    if (category !== "all") params.category = category;
    api.get("/library", { params }).then(r => setRows(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, category]);

  const openNew = () => { setForm({ ...emptyForm, kind }); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...emptyForm, ...r }); setEditId(r.id); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      if (editId) { await api.patch(`/library/${editId}`, form); toast.success("Updated"); }
      else { await api.post("/library", form); toast.success("Added to library"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this library item?")) return; await api.delete(`/library/${id}`); load(); };

  const meta = KINDS.find(x => x.k === kind);
  const categories = Array.from(new Set(rows.map(r => r.category).filter(Boolean)));

  const kindCounts = {};

  return (
    <div className="space-y-6" data-testid="library-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Masters</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold flex items-center gap-3"><LibIcon className="w-7 h-7 text-primary" /> Work & Material Library</h1>
          <p className="text-sm text-muted-foreground mt-1">Reusable masters for BOQs, Work Lists and MRL across every project.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew} data-testid="new-library-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-1.5" /> New {meta?.label.slice(0,-1) || "Item"}</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editId ? "Edit" : "New"} Library Item</DialogTitle><DialogDescription>Categorise by kind so it's easy to pull into BOQs / Work Lists / MRL.</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Select value={form.kind} onValueChange={v => setForm({...form, kind: v})}>
                <SelectTrigger data-testid="library-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>{KINDS.map(x => <SelectItem key={x.k} value={x.k}>{x.label}</SelectItem>)}</SelectContent>
              </Select></div>
              <Input className="col-span-2" placeholder="Name / Description" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="library-name-input" />
              <Input placeholder="Code (SKU)" value={form.code} onChange={e=>setForm({...form,code:e.target.value})} />
              <Input placeholder="Category" value={form.category} onChange={e=>setForm({...form,category:e.target.value})} />
              <Input placeholder="Unit" value={form.unit} onChange={e=>setForm({...form,unit:e.target.value})} />
              <Input type="number" placeholder="Rate ₹" value={form.rate} onChange={e=>setForm({...form,rate:Number(e.target.value)})} data-testid="library-rate-input" />
              <Input type="number" placeholder="GST %" value={form.gst_percent} onChange={e=>setForm({...form,gst_percent:Number(e.target.value)})} />
              <Input placeholder="Trade / Discipline" value={form.trade} onChange={e=>setForm({...form,trade:e.target.value})} />
              <Textarea className="col-span-2" rows={2} placeholder="Description / Specification" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} />
              <Textarea className="col-span-2" rows={2} placeholder="Notes" value={form.notes||""} onChange={e=>setForm({...form,notes:e.target.value})} />
            </div>
            <Button onClick={save} className="bg-primary text-primary-foreground" data-testid="save-library-btn">{editId ? "Update" : "Save to Library"}</Button>
          </DialogContent>
        </Dialog>
      </div>

      {/* Kind tabs */}
      <div className="flex gap-2 border-b border-border/50 pb-2 overflow-x-auto">
        {KINDS.map(x => {
          const active = kind === x.k;
          const Icon = x.icon;
          return (
            <button key={x.k} onClick={() => setKind(x.k)} data-testid={`library-kind-${x.k}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm border transition-all ${active ? "border-primary bg-primary/5 text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <Icon className="w-4 h-4" /> {x.label}
            </button>
          );
        })}
      </div>

      {/* Search + filter */}
      <div className="flex flex-wrap gap-2 items-center rounded-2xl bg-card border border-border/60 p-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e => e.key==="Enter" && load()} placeholder="Search by name / code / description…" className="pl-9" data-testid="library-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-52 h-9" data-testid="library-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} data-testid="library-search-btn"><Filter className="w-3.5 h-3.5 mr-1" /> Apply</Button>
      </div>

      {/* Items table */}
      <div className="rounded-2xl bg-card border border-border/60 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left">Code</th>
              <th className="text-left">Category</th>
              <th className="text-left">Unit</th>
              <th className="text-right">Rate</th>
              <th className="text-right">GST%</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`library-item-${r.id}`} className="border-t border-border/50 hover:bg-muted/20">
                <td className="px-4 py-3">
                  <div className="font-medium">{r.name}</div>
                  {r.description && <div className="text-[11px] text-muted-foreground line-clamp-1">{r.description}</div>}
                </td>
                <td className="text-xs font-mono text-muted-foreground">{r.code || "—"}</td>
                <td className="text-xs">{r.category}</td>
                <td className="text-xs text-muted-foreground">{r.unit}</td>
                <td className="text-right font-medium">₹{Number(r.rate||0).toLocaleString("en-IN",{maximumFractionDigits:2})}</td>
                <td className="text-right text-xs text-muted-foreground">{r.gst_percent}%</td>
                <td className="pr-4 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(r)} data-testid={`edit-library-${r.id}`} className="p-1.5 hover:bg-muted rounded text-primary" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(r.id)} data-testid={`delete-library-${r.id}`} className="p-1.5 hover:bg-muted rounded text-destructive" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <div className="p-10 text-center text-sm text-muted-foreground"><LibIcon className="w-8 h-8 mx-auto mb-2 opacity-40" /> No {meta?.label.toLowerCase()} yet. Click "New {meta?.label.slice(0,-1)}" to build your master library.</div>}
      </div>
    </div>
  );
}
